/* eslint-disable jsx-a11y/alt-text */
import React, { useState, useRef, useEffect } from "react";
import Bid from "./Bid";
import Ask from "./Ask";
import Ltp from "./Ltp";
import High from "./High";
import Low from "./Low";
import Change from "./Change";
import Time from "./Time";
import Symbolpl from "./Symbolpl";
import SymbolActionModal from "./SymbolActiveModal/SymbolActiveModal";
import { Trash2, TrendingUp } from "lucide-react";
import Backend from "../../../../Backend/Backend";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";

const backend = await new Backend().init();

export default function WatchlistCard({
  item,
  adv,
  removeSymbolFromList,
  activeRowId,
  setActiveRowId,
  invester,
}) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [pl, setPl] = useState(0);
  const [color, setColor] = useState("border-[var(--background)]");

  // 🧩 Swipe gesture states
  const [translateX, setTranslateX] = useState(0);
  const [isSwipedLeft, setIsSwipedLeft] = useState(false);
  const [isSwipedRight, setIsSwipedRight] = useState(false);

  const symbolType = item.type;
  // 🧩 Refs to track movement
  const startX = useRef(0);
  const startY = useRef(0);
  const isScrolling = useRef(false);

  useEffect(() => {
    if (activeRowId !== item.id) {
      setTranslateX(0); // 👈 CLOSE
    }
  }, [activeRowId]);

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    isScrolling.current = false;
  };

  const handleTouchMove = (e) => {
    const deltaX = e.touches[0].clientX - startX.current;
    const deltaY = e.touches[0].clientY - startY.current;

    // ✅ If more vertical movement → it's a scroll, not a swipe
    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      isScrolling.current = true;
      return;
    }

    // ✅ Only allow limited horizontal translation
    if (!isScrolling.current && Math.abs(deltaX) < 100) {
      setTranslateX(deltaX);
    }
  };

  const handleTouchEnd = () => {
    if (isScrolling.current) {
      resetSwipe(); // user scrolled vertically, not swiped
      return;
    }

    if (translateX > 60 || translateX < -60) {
      setActiveRowId(item.id); // 👈 OPEN THIS ROW
    } else {
      setActiveRowId(null);
    }

    if (translateX < -60) {
      setIsSwipedLeft(true);
      setIsSwipedRight(false);
      setTranslateX(-80); // show delete
    } else if (translateX > 60) {
      setIsSwipedRight(true);
      setIsSwipedLeft(false);
      setTranslateX(80); // show trade
    } else {
      resetSwipe();
    }
  };

  const resetSwipe = () => {
    setIsSwipedLeft(false);
    setIsSwipedRight(false);
    setTranslateX(0);
  };

  // 🗑️ Delete symbol
  const handleDelete = async (e) => {
    e.stopPropagation();
    resetSwipe();
    if (loading) return;
    setLoading(true);

    const data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      user_id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      symbol_id: item.id,
      symboltype: item.type,
    };

    try {
      const r = await backend.delete_symbol(data);
      setLoading(false);
      if (r.error === "False") {
        removeSymbolFromList(item.id);
        const newData = JSON.stringify(r.watchlist);
        localStorage.setItem("watchlist", newData);
      } else toast.error(r.message);
    } catch {
      setLoading(false);
      toast.error("Failed to remove symbol");
    }
  };

  const handleDeleteOptions = async () => {
    if (loading) return;
    setLoading(true);

    const data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      user_id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      symbol_id: item.id,
      symboltype: item.type,
    };

    try {
      const r = await backend.delete_symbol(data);
      setLoading(false);
      if (r.error === "False") {
        removeSymbolFromList(item.id);
        const newData = JSON.stringify(r.watchlist);
        localStorage.setItem("watchlist", newData);
      } else toast.error(r.message);
    } catch {
      setLoading(false);
      toast.error("Failed to remove symbol");
    }
  };

  // 📈 Trade
  const handleTrade = (e) => {
    e.stopPropagation();
    resetSwipe();

    window.symboldata = item;
    localStorage.setItem("symboldata", JSON.stringify(item));
    navigate("/BuySell");
  };

  const symbolSizeAdv =
    item.symbol_display.length > 14
      ? "text-[10px] font-bold"
      : item.symbol_display.length > 10
        ? "text-[13px] font-medium"
        : "text-[15px] font-medium";

  const symbolSize =
    item.symbol_display.length > 14
      ? "text-[10px] font-bold"
      : item.symbol_display.length > 10
        ? "text-[12px] font-medium"
        : "text-[14px] font-medium";

  return (
    <div className="relative overflow-hidden">
      {/* 🟩 Trade background (Right side) */}
      <div
        className="absolute left-0 top-0 bottom-0 w-20 bg-green-500 flex items-center justify-center text-white"
        onClick={handleTrade}
      >
        <TrendingUp className="w-5 h-5" />
      </div>

      {/* 🟥 Delete background (Left side) */}
      <div
        className="absolute right-0 top-0 bottom-0 w-20 bg-red-500 flex items-center justify-center text-white"
        onClick={handleDelete}
      >
        <Trash2 className="w-5 h-5" />
      </div>

      {/* 🌐 Swipeable Foreground */}
      <div
        className={`relative pr-1 bg-[var(--background)] transition-transform duration-200 ease-out`}
        style={{ transform: `translateX(${translateX}px)` }}
        onTouchStart={invester === 1 ? null : handleTouchStart}
        onTouchMove={invester === 1 ? null : handleTouchMove}
        onTouchEnd={invester === 1 ? null : handleTouchEnd}
        onClick={() => {
          if (invester === 1) return false;
          if (isSwipedLeft || isSwipedRight) return resetSwipe();
          setSelectedSymbol(item);
        }}
      >
        {item.standing === undefined ||
        item.standing === 0 ||
        item.standing === null ? null : (
          <Symbolpl
            standing={item.standing}
            setPl={setPl}
            setColor={setColor}
          />
        )}

        {adv ? (
          <div
            className={`w-full px-1 py-1 flex justify-between items-center transition-all duration-200
              ${
                item.standing !== 0 && pl
                  ? `border-l-[4px] ${color}`
                  : `border-l-[4px] border-[var(--background)]`
              }`}
          >
            {/* Left Section */}
            <div className="flex flex-col justify-around text-left">
              <span className="text-[12px] font-semibold text-[var(--subtext)] mt-0.5">
                <Change
                  symbol_id={item.symbol_id}
                  type={item.type}
                  adv={adv}
                  digit={item.digit}
                />
              </span>
              <span
                className={`flex items-center ${symbolSizeAdv} tracking-tighter font-semibold text-[var(--text)]`}
              >
                {item.symbol_display}{" "}
                <div className="text-[10px] font-semibold pl-2 text-[var(--subtext)]">
                  {item.type === "nse_cash"
                    ? "Equity"
                    : moment(item.expiry_date).format("DD-MMM")}
                </div>
              </span>
              <span className="flex items-center text-[12px] font-semibold text-[var(--subtext)] mt-0.5">
                <Time symbol_id={item.symbol_id} />
                <img
                  src={require("../../../../Image/Ch.png")}
                  alt="icon"
                  className="mx-1 w-3 h-3 object-contain"
                />
                <Ltp symbol_id={item.symbol_id} adv={adv} digit={item.digit} />
              </span>
            </div>

            {/* Right Section */}
            <div className="flex-1 flex gap-2 items-center">
              {/* 🟩 Bid Section */}
              <div className="text-right flex-1">
                <div className={`text-[var(--buymeta)]`}>
                  <Bid
                    symbol_id={item.symbol_id}
                    adv={adv}
                    digit={item.digit}
                    symbolType={symbolType}
                  />
                </div>
                <div className="text-[12px]">
                  <Low symbol_id={item.symbol_id} digit={item.digit} />
                </div>
              </div>

              {/* 🟥 Ask Section */}
              <div className="text-right flex-1">
                <div className={`text-[var(--sellmeta)]`}>
                  <Ask
                    symbol_id={item.symbol_id}
                    adv={adv}
                    digit={item.digit}
                    symbolType={symbolType}
                  />
                </div>
                <div className="text-[12px]">
                  <High symbol_id={item.symbol_id} digit={item.digit} />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`
    grid grid-cols-[2fr_1fr_1fr_1fr]
    pt-2 px-1 pb-2 text-sm items-center transition-all
    ${
      item.standing !== 0 && pl
        ? `border-l-[4px] ${color}`
        : `border-l-[4px] border-[var(--background)]`
    }
  `}
          >
            <span className={`${symbolSize} flex`}>
              <div className="font-semibold break-words leading-tight max-w-[120px]">
                {item.symbol_display}
              </div>
              <div className="text-[9px] font-semibold text-[var(--text)] ml-1">
                {item.type === "nse_cash"
                  ? "Equity"
                  : item.type === "mcx_options" || item.type === "fo"
                    ? null
                    : moment(item.expiry_date).format("DD-MMM")}
              </div>
            </span>

            <Bid symbol_id={item.symbol_id} />
            <Ask symbol_id={item.symbol_id} />
            <Change symbol_id={item.symbol_id} type={item.type} />
          </div>
        )}
      </div>

      {/* ⚡ Modal */}
      <SymbolActionModal
        isOpen={!!selectedSymbol}
        onClose={() => setSelectedSymbol(null)}
        selectedSymbol={selectedSymbol}
        handleDelete={handleDeleteOptions}
      />
    </div>
  );
}
